import { Component, OnDestroy, OnInit } from '@angular/core';
import { Shift, ShiftType } from '@model/reservation';
import { Store, select } from '@ngrx/store';
import { TablesPlanActions } from '@store/actions/tables-plan.actions';
import { State } from '@store/index';
import * as AppSelectors from '@store/selectors/app.selectors';
import * as ReservationListSelectors from '@store/selectors/reservation-list.selectors';
import * as TablesPlanSelectors from '@store/selectors/tables-plan.selectors';
import { Observable, Subject, takeUntil } from 'rxjs';
import { CapacitorStorageService } from '../lib/capacitor-storage.service';
import { FontSize } from '../settings-form/settings-form.component';
import * as ReservationListActions from '../store/actions/reservation-list.actions';

@Component({
  selector: 'app-tables-plan',
  templateUrl: './tables-plan.component.html',
  styleUrl: './tables-plan.component.scss'
})
export class TablesPlanComponent implements OnInit, OnDestroy {

  constructor(
    private capacitorStorageService: CapacitorStorageService,
    private store: Store<State>,
  ) {

  }

  destroy$: Subject<boolean> = new Subject<boolean>();

  editMode$: Observable<boolean> = this.store.pipe(select(TablesPlanSelectors.selectEditMode));
  editMode: boolean = false;
  processedShifts$: Observable<Shift[]> = this.store.pipe(select(ReservationListSelectors.selectProcessedShifts));
  processedShifts: Shift[] = [];
  selectedShift$: Observable<Shift | null | undefined> = this.store.pipe(select(ReservationListSelectors.selectSelectedShift));
  selectedShift: Shift | null | undefined = null;
  sidePanelCollapsed$: Observable<boolean> = this.store.pipe(select(TablesPlanSelectors.selectSidePanelCollapsed));
  sidePanelCollapsed: boolean = false;
  tablesPlanFontSize$: Observable<FontSize> = this.store.pipe(select(AppSelectors.selectTablesPlanFontSize));
  tablesPlanFontSize: FontSize = FontSize.Default;
  isDirty$: Observable<boolean> = this.store.pipe(select(TablesPlanSelectors.selectIsDirty));
  isDirty: boolean = false;

  ngOnInit(): void {

    this.capacitorStorageService.setLatestActiveView('TablesPlan');

    this.store.dispatch({ type: TablesPlanActions.loadTablesPlanOptions.type });

    this.store.dispatch({ type: TablesPlanActions.setHasOpenedTablesPlan.type, hasOpenedTablesPlan: true });

    // load data
    this.store.dispatch({ type: TablesPlanActions.loadTablesPlan.type });

    this.store.dispatch({ type: ReservationListActions.loadReservations.type });

    this.editMode$.pipe(takeUntil(this.destroy$)).subscribe((editMode) => {
      this.editMode = editMode;
    });

    this.selectedShift$.pipe(takeUntil(this.destroy$)).subscribe((selectedShift: Shift | null | undefined) => {
      this.selectedShift = selectedShift;
    });

    this.isDirty$.pipe(takeUntil(this.destroy$)).subscribe((isDirty) => {
      this.isDirty = isDirty;
    });

    this.processedShifts$.pipe(takeUntil(this.destroy$)).subscribe((processedShifts: Shift[]) => {
      this.processedShifts = processedShifts;

      if (processedShifts.length > 0 && this.selectedShift == null) {
        const shift = processedShifts.find(s => s.type === ShiftType.AllDay) ?? processedShifts[0];
        // set Shift
        this.store.dispatch({ type: ReservationListActions.setSelectedShift.type, shift });
      }
    });

    this.sidePanelCollapsed$.pipe(takeUntil(this.destroy$)).subscribe((sidePanelCollapsed: boolean) => {
      this.sidePanelCollapsed = sidePanelCollapsed;
    });

    this.tablesPlanFontSize$.pipe(takeUntil(this.destroy$)).subscribe((tablesPlanFontSize) => {
      this.tablesPlanFontSize = tablesPlanFontSize;
    });
  }

  refresh(): Promise<void> {
    // load reservations
    this.store.dispatch({ type: TablesPlanActions.loadTablesPlan.type });
    return Promise.resolve();
  }

  ngOnDestroy(): void {
    // reset any editing relative state variables
    this.store.dispatch({ type: TablesPlanActions.resetTablesPlanEditingState.type });

    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }
}
